// @flow strict

import * as React from 'react'
import cx from 'classnames'
import {
  Formik,
  Field,
  Form,
  type FormikProps,
  ErrorMessage,
  getIn,
} from 'formik'
import {
  type ResidentialStatus,
  type EmploymentStatus,
  type NumDependent,
  type FormState,
  type FormResult,
  type RightToReside,
} from './formTypes'

import {
  formStateToEligibilityCheckerRequest,
  eligibilityCheckerFormSchema,
  desiredLimitValues,
} from './formUtils'
import styles from './OverdraftEligibilityChecker.module.scss'
import { Button } from '@mondough/public-web-components'
const {
  form,
  formGroup,
  formGroupHalfCol,
  errorMessage,
  inlineLabel,
  blockLabel,
  inputHelperText,
  formSectionHeading,
  twoColumnContainer,
  hintText,
} = styles

export const residentialStatus: { [k: string]: ResidentialStatus } = {
  TENANT: 'tenant',
  MORTGAGE: 'mortgage',
  NO_MORTGAGE: 'no-mortgage',
  LIVING_WITH_PARENTS: 'living-with-parents',
  OTHER: 'other',
}

const prettyStatus: { [k: EmploymentStatus]: string } = {
  full_time: 'Full Time',
  not_employed: 'Unemployed',
  part_time: 'Part Time',
  retired: 'Retired',
  self_employed: 'Self Employed',
  student: 'Student',
}

export const employmentStatus: { [k: string]: EmploymentStatus } = {
  FULL_TIME: 'full_time',
  PART_TIME: 'part_time',
  RETIRED: 'retired',
  SELF_EMPLOYED: 'self_employed',
  STUDENT: 'student',
  UNEMPLOYED: 'not_employed',
}

const prettyResidentialStatus: { [k: ResidentialStatus]: string } = {
  tenant: 'Tenant',
  mortgage: 'Owner - Mortgage',
  'no-mortgage': 'Owner - No Mortgage',
  'living-with-parents': 'Living with parents',
  other: 'Other',
}

export const numDependents: { [k: string]: NumDependent } = {
  ZERO: '0',
  ONE: '1',
  TWO: '2',
  THREE: '3',
  FOUR: '4',
  FIVE: '5',
  SIX_PLUS: '6',
}

const prettyNumDependents: { [k: NumDependent]: string } = {
  '0': '0',
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6+',
}

export const rightToReside: { [k: string]: RightToReside } = {
  INDEFINITE: 'indefinite',
  TEMPORARY: 'temporary',
  NO_RIGHT: 'no-right',
}

const prettyRightToReside: { [k: RightToReside]: string } = {
  indefinite: 'I have the right to live in the UK permanently',
  temporary: 'I have the right to live in the UK temporarily',
  'no-right': 'I donʼt have the right to live in the UK',
}

const fieldLabels = {
  age: 'How old are you?',
  employmentStatus: "What's your employment status?",
  residentialStatus: "What's your residential status?",
  postcode: "What's your postcode?",
  numDependents:
    'How many people rely on you financially? Like your partner, children or relatives?',
  ukNational: 'Are you a British citizen?',
  rightToReside: 'How long do you have the right to live in the UK for?',
  rightToResideExpiry: 'When does your right to live here end?',
  hasPreviousCredit:
    'Have you had a bank account with an overdraft or a credit card in the UK for more than a year?',
  hasPreviousJudgementOrDefault:
    'Have you got a county court judgement or defaulted on some borrowing in the past 6 years?',
  hasMissedPayments:
    'Have you missed any payments on borrowing in the last 2 years?',
  income: "What's your annual income before tax?",
  reducedIncome:
    'Is your income likely to go down in the near future? What is your expected annual income before tax?',
  desiredLimit: 'How much would you like your overdraft limit to be?',
  'existingDebt.creditBalance':
    'Do you have a credit card? How much credit are you currently using?',
  'existingDebt.overdraftBalance':
    'Do you have an overdraft elsewhere? How much are you currently using?',
  'existingDebt.fixedMonthlyPayments':
    'How much do you pay off monthly in loans, car financing, etc?',
}

const housingExpenditureLabels = {
  tenant: "What's your monthly share of the rent?",
  mortgage: "What's your monthly share of the mortgage?",
  living_with_parents: "What's your monthly rent contribution?",
  other: "What's your monthly accomodation cost?",
}

const emptyInitialValues: FormState = {
  age: '',
  employmentStatus: 'full_time',
  residentialStatus: 'tenant',
  postcode: '',
  hasPreviousCredit: undefined,
  hasPreviousJudgementOrDefault: undefined,
  hasMissedPayments: undefined,
  income: '',
  reducedIncome: '',
  numDependents: '0',
  housingExpenditure: '',
  desiredLimit: 0,
  existingDebt: {
    creditBalance: '',
    overdraftBalance: '',
    fixedMonthlyPayments: '',
  },
}

const BASE_URL = process.env.GATSBY_API_BASE_PATH
const OAUTH_ID = process.env.GATSBY_OAUTH_ID
const OAUTH_SECRET = process.env.GATSBY_OAUTH_SECRET

const submitForm = (values, setIsLoading, setResult) => {
  setIsLoading(true)
  fetch(`${BASE_URL}/oauth2/token`, {
    body: new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: OAUTH_ID,
      client_secret: OAUTH_SECRET,
    }),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      Accept: 'application/json',
    },
  })
    .then(async result => {
      const { access_token } = await result.json()
      fetch(`${BASE_URL}/lending-eligibility-checker/overdraft-eligibility`, {
        method: 'PUT',
        body: JSON.stringify(formStateToEligibilityCheckerRequest(values)),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${access_token}`,
        },
      })
        .then(async result => {
          const response = await result.json()
          setResult({
            eligibility: response.Eligibility,
            eligibility_explanation: response.EligibilityExplanation,
          })
          setIsLoading(false)
        })
        .catch(err => {
          setIsLoading(false)
          setResult({
            eligibility: 'oops',
            eligibility_explanation:
              'Something went wrong, please check your internet connection and try again.',
          })
          console.error(err)
        })
    })
    .catch(err => {
      setIsLoading(false)
      setResult({
        eligibility: 'oops',
        eligibility_explanation:
          'Something went wrong, please check your internet connection and try again.',
      })
      console.error(err)
    })
}

const OverdraftEligibilityCheckerForm = ({
  setIsLoading,
  setResult,
}: {
  setIsLoading(boolean): void,
  setResult(FormResult): void,
}) => (
  <Formik
    initialValues={emptyInitialValues}
    validationSchema={eligibilityCheckerFormSchema()}
    onSubmit={values => submitForm(values, setIsLoading, setResult)}
  >
    {({
      handleSubmit,
      handleReset,
      submitCount,
      errors,
      ...props
    }: FormikProps<FormState>) => (
      <Form className={form}>
        <h3 className={formSectionHeading}>Personal Questions </h3>
        <div className={twoColumnContainer}>
          <Field name="age">
            {({ field, form }) => (
              <div className={cx(formGroup, formGroupHalfCol)}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <input {...field} type="number" id={field.name} />
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field name="employmentStatus">
            {({ field, form }) => (
              <div className={cx(formGroup, formGroupHalfCol)}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <select {...field} id={field.name}>
                  {Object.keys(employmentStatus).map(key => {
                    return (
                      <option
                        value={employmentStatus[key]}
                        key={employmentStatus[key]}
                      >
                        {prettyStatus[employmentStatus[key]]}
                      </option>
                    )
                  })}
                </select>
              </div>
            )}
          </Field>
          <Field name="residentialStatus">
            {({ field, form }) => (
              <div className={cx(formGroup, formGroupHalfCol)}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <select {...field} id={field.name}>
                  {Object.keys(residentialStatus).map(key => {
                    return (
                      <option
                        value={residentialStatus[key]}
                        key={residentialStatus[key]}
                      >
                        {prettyResidentialStatus[residentialStatus[key]]}
                      </option>
                    )
                  })}
                </select>
              </div>
            )}
          </Field>
          <Field name="housingExpenditure">
            {({ field, form }) =>
              form.values.residentialStatus !== 'no-mortgage' && (
                <div className={cx(formGroup, formGroupHalfCol)}>
                  <label className={blockLabel} htmlFor={field.name}>
                    {housingExpenditureLabels[form.values.residentialStatus]}
                  </label>
                  <div className={styles.flexFormFieldWithSuffix}>
                    <span className={inputHelperText}>£</span>
                    <input {...field} type="number" id={field.name} />
                    <span className={inputHelperText}>/ month</span>
                  </div>
                  {field.name && getIn(errors, field.name) && (
                    <div className={errorMessage}>
                      <ErrorMessage name={field.name} />
                    </div>
                  )}
                </div>
              )
            }
          </Field>
          <Field name="postcode">
            {({ field, form }) => (
              <div className={cx(formGroup, formGroupHalfCol)}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <input {...field} type="text" id={field.name} />
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field name="income">
            {({ field, form }) => (
              <div className={cx(formGroup, formGroupHalfCol)}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <div className={styles.flexFormFieldWithSuffix}>
                  <span className={inputHelperText}>£</span>
                  <input {...field} type="number" id={field.name} />
                  <span className={inputHelperText}>/ year</span>
                </div>
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
        </div>
        <div>
          <Field name="reducedIncome">
            {({ field, form }) => (
              <div className={formGroup}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <div className={cx(formGroup, formGroupHalfCol)}>
                  <div className={styles.flexFormFieldWithSuffix}>
                    <span className={inputHelperText}>£</span>
                    <input {...field} type="number" id={field.name} />
                    <span className={inputHelperText}>/ year</span>
                  </div>
                </div>
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field name="numDependents">
            {({ field, form }) => (
              <div className={formGroup}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <div className={cx(formGroup, formGroupHalfCol)}>
                  <select {...field} id={field.name}>
                    {Object.keys(numDependents).map(key => {
                      return (
                        <option
                          value={numDependents[key]}
                          key={numDependents[key]}
                        >
                          {prettyNumDependents[numDependents[key]]}
                        </option>
                      )
                    })}
                  </select>
                </div>
              </div>
            )}
          </Field>
          <Field name="ukNational">
            {({ field, form }) => (
              <div className={formGroup}>
                <p className={styles.radioQuestions}>
                  {fieldLabels[field.name]}
                </p>
                <label className={inlineLabel} htmlFor={field.name + 'Yes'}>
                  Yes
                </label>
                <input
                  {...field}
                  type="radio"
                  id={field.name + 'Yes'}
                  value="Yes"
                />
                <label className={inlineLabel} htmlFor={field.name + 'No'}>
                  No
                </label>
                <input
                  {...field}
                  type="radio"
                  id={field.name + 'No'}
                  value="No"
                />
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field name="rightToReside">
            {({ field, form }) =>
              form.values.ukNational === 'No' && (
                <div className={formGroup}>
                  <label className={blockLabel} htmlFor={field.name}>
                    {fieldLabels[field.name]}
                  </label>
                  <div className={cx(formGroup, formGroupHalfCol)}>
                    <select {...field} id={field.name}>
                      {Object.keys(rightToReside).map(key => {
                        return (
                          <option
                            value={rightToReside[key]}
                            key={rightToReside[key]}
                          >
                            {prettyRightToReside[rightToReside[key]]}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                </div>
              )
            }
          </Field>
          <Field name="rightToResideExpiry">
            {({ field, form }) =>
              form.values.rightToReside === 'temporary' && (
                <div className={cx(formGroup, formGroupHalfCol)}>
                  <label className={blockLabel} htmlFor={field.name}>
                    {fieldLabels[field.name]}
                  </label>
                  <input
                    {...field}
                    type="number"
                    id={field.name}
                    defaultValue={new Date().getFullYear()}
                  />
                  {field.name && getIn(errors, field.name) && (
                    <div className={errorMessage}>
                      <ErrorMessage name={field.name} />
                    </div>
                  )}
                </div>
              )
            }
          </Field>
        </div>
        <Field name="hasPreviousCredit">
          {({ field, form }) => (
            <div className={formGroup}>
              <p className={styles.radioQuestions}>{fieldLabels[field.name]}</p>
              <label className={inlineLabel} htmlFor={field.name + 'Yes'}>
                Yes
              </label>
              <input
                {...field}
                type="radio"
                id={field.name + 'Yes'}
                value="Yes"
              />
              <label className={inlineLabel} htmlFor={field.name + 'No'}>
                No
              </label>
              <input
                {...field}
                type="radio"
                id={field.name + 'No'}
                value="No"
              />
              {field.name && getIn(errors, field.name) && (
                <div className={errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>
        <Field name="hasPreviousJudgementOrDefault">
          {({ field, form }) => (
            <div className={formGroup}>
              <p className={styles.radioQuestions}>{fieldLabels[field.name]}</p>
              <label className={inlineLabel} htmlFor={field.name + 'Yes'}>
                Yes
              </label>
              <input
                {...field}
                type="radio"
                id={field.name + 'Yes'}
                value="Yes"
              />
              <label className={inlineLabel} htmlFor={field.name + 'No'}>
                No
              </label>
              <input
                {...field}
                type="radio"
                id={field.name + 'No'}
                value="No"
              />
              {field.name && getIn(errors, field.name) && (
                <div className={errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>
        <Field name="hasMissedPayments">
          {({ field, form }) => (
            <div className={formGroup}>
              <p className={styles.radioQuestions}>{fieldLabels[field.name]}</p>
              <label className={inlineLabel} htmlFor={field.name + 'Yes'}>
                Yes
              </label>
              <input
                {...field}
                type="radio"
                id={field.name + 'Yes'}
                value="Yes"
              />
              <label className={inlineLabel} htmlFor={field.name + 'No'}>
                No
              </label>
              <input
                {...field}
                type="radio"
                id={field.name + 'No'}
                value="No"
              />
              {field.name && getIn(errors, field.name) && (
                <div className={errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>
        <h3 className={formSectionHeading}>Existing Debt</h3>
        <div className={twoColumnContainer}>
          <Field name="existingDebt.creditBalance">
            {({ field, form }) => (
              <div className={cx(formGroup, formGroupHalfCol)}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <div className={styles.flexFormFieldWithSuffix}>
                  <span className={inputHelperText}>£</span>
                  <input {...field} type="number" id={field.name} />
                </div>
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field name="existingDebt.overdraftBalance">
            {({ field, form }) => (
              <div className={cx(formGroup, formGroupHalfCol)}>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <div className={styles.flexFormFieldWithSuffix}>
                  <span className={inputHelperText}>£</span>
                  <input {...field} type="number" id={field.name} />
                </div>
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
        </div>
        <Field name="existingDebt.fixedMonthlyPayments">
          {({ field, form }) => (
            <div className={formGroup}>
              <label className={blockLabel} htmlFor={field.name}>
                {fieldLabels[field.name]}
              </label>
              <p className={hintText}>
                This includes any other financing options (e.g. paying off a new
                phone monthly)
              </p>
              <div className={cx(formGroup, formGroupHalfCol)}>
                <div className={styles.flexFormFieldWithSuffix}>
                  <span className={inputHelperText}>£</span>
                  <input {...field} type="number" id={field.name} />
                  <span className={inputHelperText}>/ month</span>
                </div>
              </div>
              {field.name && getIn(errors, field.name) && (
                <div className={errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>
        <div className={cx(formGroup, styles.sliderField)}>
          <Field name="desiredLimit">
            {({ field, form }) => (
              <>
                <label className={blockLabel} htmlFor={field.name}>
                  {fieldLabels[field.name]}
                </label>
                <div className={styles.sliderContainer}>
                  <input
                    {...field}
                    type="range"
                    id={field.name}
                    step="1"
                    min="0"
                    max={desiredLimitValues.length - 1}
                    className={styles.slider}
                  />
                  {desiredLimitValues[field.value] && (
                    <span className={inputHelperText}>
                      {'£' + desiredLimitValues[field.value]}
                    </span>
                  )}
                </div>
                {field.name && getIn(errors, field.name) && (
                  <div className={errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </>
            )}
          </Field>
          <Button type="submit" theme="primary">
            {submitCount === 0 ? 'Check eligibility' : 'Check again'}
          </Button>
        </div>
        {errors && Object.keys(errors).length ? (
          <div className={errorMessage}>Please fix the errors above</div>
        ) : null}
      </Form>
    )}
  </Formik>
)

export default OverdraftEligibilityCheckerForm
